/**
 * Per-trade confirmation controller — the reconciliation state machine shared
 * by `useBuyTrade` / `useSellTrade` / `useSwapTrade`.
 *
 * It races two ground-truth signals for "the trade landed on chain":
 *   1. the indexer's NATS trade event (fast — fires ~chain-time), via
 *      {@link registerTradeWatch}, and
 *   2. the Turnkey/relay submit pipeline's own confirm (slow — gated by the
 *      Turnkey signature poll), surfaced by the hook calling `resolveFromTurnkey`.
 *
 * Whichever wins fires the success UX EXACTLY ONCE (the hook supplies the
 * side-effects via `fireSuccess`). The other path then only reconciles the
 * displayed signature (Turnkey's is authoritative — base58 from the chain).
 *
 * Safety:
 *  - `fireSuccess` runs at most once → the balance patch / success toast / track
 *    happen exactly once, no double-count.
 *  - A NATS match is the indexer's proof the tx landed (the indexer only
 *    publishes a trade event for a committed on-chain trade). So `awaitLanding`
 *    lets the hook KEEP success even when Turnkey's client leg errors
 *    (`Failed to fetch`) — the trade demonstrably landed.
 *  - Nothing here ever re-submits, re-signs, or aborts the on-chain attempt.
 */

import { registerTradeWatch, type TradeWatchInput } from './user-trade-watch.js';

/**
 * How long the failure path waits for a (late) NATS landing signal before
 * declaring a genuine failure. The indexer publishes within ~1s of landing, so
 * a real landing is almost always already matched by the time Turnkey errors;
 * this bounds the wait for the narrow "Turnkey errored fast, event still in
 * flight" case.
 */
export const LANDING_GRACE_MS = 8_000;

/**
 * Codes where the transaction was SENT to the chain but its on-chain outcome is
 * UNKNOWN to the client — the only failures where waiting for the indexer's
 * NATS landing signal is justified (the tx may actually have landed).
 *
 * Everything NOT in this set is decided immediately:
 *  - pre-send / rejected codes (sim, sign, encode, quota, rate-limit, relay
 *    reject/invalid, not-configured, alt-blocked, fee-payer-low, priority-fee)
 *    never reached the chain → genuine failure now (no grace wait, no lingering
 *    pending entry to widen the stale-match window).
 *  - `TX_CONFIRM_FAILED` is a definitive on-chain REVERT (landed and failed) →
 *    genuine failure now; the indexer publishes no event for a revert.
 */
const AMBIGUOUS_LANDING_CODES: ReadonlySet<string> = new Set([
  'TX_CONFIRM_TIMEOUT',
  'TX_TURNKEY_TIMEOUT',
  'TX_TURNKEY_FAILED',
  'TX_RELAY_TIMEOUT',
  'TX_BLOCKHASH_EXPIRED',
  'TX_MAX_RETRIES',
  'TX_UNKNOWN',
]);

/** True when a submit-failure leaves the on-chain landing genuinely unknown
 *  (tx was sent) — wait for the NATS landing signal. False for pre-send /
 *  rejected / definitive-revert codes, which are decided immediately. */
export function isAmbiguousLandingError(code: string): boolean {
  return AMBIGUOUS_LANDING_CODES.has(code);
}

export interface TradeConfirmCallbacks {
  /**
   * Render the full success UX. Called AT MOST ONCE. `signature` is null only
   * for an AMM NATS match (no sig on the wire) — reconciled later via
   * `reconcileSignature`. `viaNats` distinguishes the early NATS path from the
   * Turnkey-driven path (used for the measurable log line).
   */
  fireSuccess: (signature: string | null, viaNats: boolean, elapsedMs: number) => void;
  /** Update only the displayed signature (no re-patch / re-track). */
  reconcileSignature: (signature: string) => void;
}

export interface TradeConfirmController {
  wasFired: () => boolean;
  firedSignature: () => string | null;
  /** Drive from the Turnkey/relay confirm. Fires success if NATS hasn't; else
   *  reconciles the authoritative signature. */
  resolveFromTurnkey: (signature: string) => void;
  /** Wait up to `graceMs` for a (late) NATS landing match. Resolves true iff a
   *  match has fired success (now or earlier). */
  awaitLanding: (graceMs: number) => Promise<boolean>;
  /** Release the NATS subscription refcount + drop the pending entry. */
  release: () => void;
}

function delay(ms: number): Promise<null> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(null), ms);
  });
}

export function createTradeConfirm(
  input: TradeWatchInput,
  cb: TradeConfirmCallbacks,
): TradeConfirmController {
  const watch = registerTradeWatch(input);
  let fired = false;
  let firedSig: string | null = null;

  const fire = (signature: string | null, viaNats: boolean): void => {
    if (fired) return;
    fired = true;
    firedSig = signature;
    cb.fireSuccess(signature, viaNats, Date.now() - input.submitStartMs);
  };

  // Eager NATS path: fire the instant the indexer publishes the trade.
  watch.matched
    .then((m) => {
      if (m !== null) fire(m.signature, true);
    })
    .catch(() => {
      /* registry promise never rejects; defensive no-op */
    });

  return {
    wasFired: (): boolean => fired,
    firedSignature: (): string | null => firedSig,
    resolveFromTurnkey: (signature: string): void => {
      if (!fired) {
        fire(signature, false);
        return;
      }
      if (firedSig !== signature) {
        firedSig = signature;
        cb.reconcileSignature(signature);
      }
    },
    awaitLanding: async (graceMs: number): Promise<boolean> => {
      if (fired) return true;
      const m = await Promise.race([watch.matched, delay(graceMs)]);
      if (m !== null && !fired) fire(m.signature, true);
      return fired;
    },
    release: (): void => watch.cancel(),
  };
}
